import React, { useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaFilePdf,
  FaPrint,
  FaRegFileExcel,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { MwSpinner, NoDataFound } from "../../components/ui";
import MwButton from "../../components/ui/MwButton";
import { useFetch } from "../../hooks";
import handleConvertToPDF from "../../utils/handleConvertToPDF";
import { MdEditNote } from "react-icons/md";
import * as XLSX from "xlsx";
import CardHeadeWithActions from "../../components/ui/CardHeadeWithActions";
import { LuLayoutDashboard } from "react-icons/lu";

const TableStocking = () => {
  const [t] = useTranslation("global");
  const { id, tenant } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();

  const {
    data: stockTakingMasterResult,
    loading: loadingStockTakingMaster,
  } = useFetch(
    `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/stocktaking/master/${id}`
  );

  const handlePrint = () => {
    const printContents =
      document.getElementById("printable-content").outerHTML;

    const printWindow = window.open("", "_blank");

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
        <head>
          <title>جرد المخزون</title>

          <style>
            @page {
              size: A4;
              margin: 1.5cm;
            }

            body {
              font-family: Arial, sans-serif;
              direction: rtl;
              color: #000;
              padding: 0;
              margin: 0;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th, td {
              border: 1px solid #ddd;
              padding: 10px;
            }

            th {
              background: #251658;
              color: white;
            }
          </style>
        </head>

        <body>
          ${printContents}

          <script>
            window.onload = function () {
              window.print();

              window.onafterprint = function () {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleEdit = () => {
    navigate(`/${tenant}/stocktakings/show/${id}`);
  };

  const handleCancel = () => {
    navigate(`/${tenant}/stocktakings`);
  };

  if (loadingStockTakingMaster) {
    return (
      <div className="flex justify-center items-center h-40">
        <MwSpinner />
      </div>
    );
  }

  const {
    details,
    warehouse,
    code,
    completed,
  } = stockTakingMasterResult?.data || {};

  if (!stockTakingMasterResult?.data) {
    return (
      <NoDataFound msg={t("stocktaking.no_data_available")} />
    );
  }

  const exportExcel = () => {
    if (details?.length > 0) {
      const wb = XLSX.utils.book_new();

      const ws = XLSX.utils.json_to_sheet(details);

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Warehouse Report"
      );

      XLSX.writeFile(
        wb,
        `warehouse_inventory_${code}.xlsx`
      );
    }
  };

  return (
    <div className="rounded-2xl bg-[#25165808]  border-[#25165815]">

      {/* Header */}
      <CardHeadeWithActions
        title={t("stocktaking.warehouse_inventory")}
        icon={
          <LuLayoutDashboard className="w-6 h-6 text-[#251658]" />
        }
      >
        <div className="flex items-center gap-2">

          <MwButton
            actionType="button"
            inGroup={true}
            type="saveBtn"
            onClick={handleCancel}
          >
            {t("stocktaking.Inventorylist")}
          </MwButton>

          {!completed && (
            <MwButton
              actionType="button"
              size="sm"
              inGroup={true}
              type="saveBtn"
              onClick={handleEdit}
            >
              <MdEditNote />
              {t("stocktaking.edit_add")}
            </MwButton>
          )}
        </div>
      </CardHeadeWithActions>

      {/* Content */}
      <div className="rounded-2xl px-5 bg-white shadow-sm mt-2 border border-[#25165815]">

        {/* Toolbar */}
        <div className="mb-6 w-full flex justify-between items-center py-5 border-b border-[#25165810]">

          <ul className="flex flex-wrap gap-2">

            <li
              onClick={exportExcel}
              className="px-3 py-2 rounded-xl flex items-center gap-2 bg-[#25165808] text-[#251658] hover:bg-[#25165815] transition-all duration-300 hover:shadow-sm cursor-pointer"
            >
              <FaRegFileExcel />
              <span className="text-sm">
                {t("reports.Export_Excel")}
              </span>
            </li>

            <li
              onClick={() =>
                handleConvertToPDF({
                  lang: "ar",
                  elementId: "printable-content",
                  fileName: `جرد_المخزون_${code}.pdf`,
                })
              }
              className="px-3 py-2 rounded-xl flex items-center gap-2 bg-[#25165808] text-[#251658] hover:bg-[#25165815] transition-all duration-300 hover:shadow-sm cursor-pointer"
            >
              <FaFilePdf />
              <span className="text-sm">
                {t("reports.Save_as_PDF")}
              </span>
            </li>

            <li
              onClick={handlePrint}
              className="px-3 py-2 rounded-xl flex items-center gap-2 bg-[#25165808] text-[#251658] hover:bg-[#25165815] transition-all duration-300 hover:shadow-sm cursor-pointer"
            >
              <FaPrint />
              <span className="text-sm">
                {t("reports.Print")}
              </span>
            </li>
          </ul>
        </div>

        {/* Status */}
        {completed && (
          <div className="bg-[#25165810] text-[#251658] p-4 mb-5 rounded-2xl text-center border border-[#25165820]">
            <span className="font-semibold">
              {t("stocktaking.Settlement_Completed")}
            </span>
          </div>
        )}

        {/* Printable */}
        <div
          id="printable-content"
          ref={printRef}
          className="p-6 bg-white h-[calc(100vh-330px)] overflow-y-auto custom-scrollbar"
        >

          {/* Header */}
          <div className="text-center mb-10 border-b border-[#25165820] pb-6">

            <h1 className="text-3xl font-bold mb-3 text-[#251658]">
              {t("stocktaking.title")}
            </h1>

            <div className="text-slate-600">
              {t("stocktaking.code")} : {code}
            </div>

            <div className="text-slate-600 mt-1">
              {t("stocktaking.date")} :
              {" "}
              {new Date().toLocaleDateString("ar-EG")}
            </div>
          </div>

          {/* Warehouse Info */}
          <div className="mb-8 border border-[#25165815] p-5 rounded-2xl bg-[#25165805]">

            <h2 className="text-lg font-semibold mb-5 text-[#251658]">
              {t("stocktaking.warehouse_info")}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              <div>
                <span className="font-semibold text-[#251658]">
                  {t("stocktaking.warehouse_name")} :
                </span>

                <span className="text-slate-600 mr-2">
                  {warehouse?.name_ar || ""}
                </span>
              </div>

              <div>
                <span className="font-semibold text-[#251658]">
                  {t("stocktaking.warehouse_code")} :
                </span>

                <span className="text-slate-600 mr-2">
                  {warehouse?.code || ""}
                </span>
              </div>

              <div className="md:col-span-2">
                <span className="font-semibold text-[#251658]">
                  {t("stocktaking.warehouse_address")} :
                </span>

                <span className="text-slate-600 mr-2">
                  {warehouse?.address || ""}
                </span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-2xl border border-[#25165815]">

            {details?.length > 0 ? (
              <table className="w-full border-collapse text-sm">

                <thead>
                  <tr className="bg-[#251658] text-white">

                    <th className="p-4 text-center">
                      {t("stocktaking.product_name")}
                    </th>

                    <th className="p-4 text-center">
                      {t("stocktaking.system_balance")}
                    </th>

                    <th className="p-4 text-center">
                      {t("stocktaking.actual_balance")}
                    </th>

                    <th className="p-4 text-center">
                      {t("stocktaking.sale_price")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {details.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-[#25165808] transition-all duration-200 border-b"
                    >
                      <td className="p-4 text-slate-700">
                        {item.product_name}
                      </td>

                      <td className="p-4 text-center text-slate-700">
                        {item.system_balance}
                      </td>

                      <td className="p-4 text-center text-slate-700">
                        {item.stock_balance}
                      </td>

                      <td className="p-4 text-center text-slate-700">
                        {item.price_sale || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <NoDataFound
                msg={t("stocktaking.no_data_available")}
              />
            )}
          </div>

          {/* Footer */}
          <div className="mt-16 grid grid-cols-2 gap-10 text-center">

            <div className="space-y-10">
              <div className="font-semibold text-[#251658]">
                {t("stocktaking.prepared_by")}
              </div>

              <div className="border-t border-[#25165830] pt-2 text-[#25165899]">
                {t("stocktaking.signature")}
              </div>
            </div>

            <div className="space-y-10">
              <div className="font-semibold text-[#251658]">
                {t("stocktaking.approved_by")}
              </div>

              <div className="border-t border-[#25165830] pt-2 text-[#25165899]">
                {t("stocktaking.signature")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableStocking;